/**
 * Telling a deliberate sign-in refusal from a sign-in that broke.
 *
 * Two of the ways sign-in ends are decisions the app made on purpose: the
 * address is not one anybody allowlisted, or the account was deactivated after
 * its owner left. Both used to arrive here as the same opaque popup failure and
 * be shown as "an error occurred" — so a person who simply needs to be added
 * was told the site was broken, and retried instead of asking.
 *
 * **How the reason gets here.** A blocking function's only channel to the
 * client is the message of the error it raises. Identity Platform wraps that
 * string in its own envelope and the Firebase JS SDK re-wraps it again, so what
 * reaches us is an `auth/internal-error` whose `message` contains something
 * like:
 *
 * ```
 * Firebase: HTTP Cloud Function returned an error: {"error":{"code":400,
 * "message":"BLOCKING_FUNCTION_ERROR_RESPONSE : [NAV:DEACTIVATED] User …",
 * "status":"INVALID_ARGUMENT"}} (auth/internal-error).
 * ```
 *
 * Nothing in that is a field we control except our own text, which is why the
 * backend puts a bracketed token at the front of it and why this looks for the
 * token by substring rather than parsing the envelope. Neither wrapper's shape
 * is contractual; the token is.
 *
 * The other half of the contract is `sign_in_refusal.py`, and the tokens are
 * what couple them — two languages reading one string, so no test can assert
 * they agree. Adding a case means adding it in both places, plus the message
 * `signInRefusalKey` names.
 */

/** Mirrors `SignInRefusal` in `sign_in_refusal.py`. */
export type SignInRefusal = "NOT_ALLOWLISTED" | "DEACTIVATED";

/** Mirrors `refusal_message`'s tag. */
const TOKEN: Record<SignInRefusal, string> = {
  NOT_ALLOWLISTED: "[NAV:NOT_ALLOWLISTED]",
  DEACTIVATED: "[NAV:DEACTIVATED]",
};

/**
 * The refusal a failed sign-in carries, or null if it carries none.
 *
 * Null is the answer for a genuine fault *and* for a refusal shape we do not
 * know, and both should stay generic: inventing a specific reason for a failure
 * we cannot identify is the mistake this module exists to undo, only pointed
 * the other way.
 */
export function signInRefusalOf(error: unknown): SignInRefusal | null {
  // Read off the whole error rather than `.message` alone: the SDK has put our
  // text in `message` for every case we have seen, but it also keeps the raw
  // server response on `customData._serverResponse`, and a token found in
  // either is the same fact. Stringifying covers both without depending on
  // which one holds it.
  const text = describe(error);
  if (!text) return null;
  for (const [refusal, token] of Object.entries(TOKEN)) {
    if (text.includes(token)) return refusal as SignInRefusal;
  }
  return null;
}

function describe(error: unknown): string {
  if (typeof error === "string") return error;
  if (!error || typeof error !== "object") return "";
  const parts: string[] = [];
  const message = (error as { message?: unknown }).message;
  if (typeof message === "string") parts.push(message);
  const custom = (error as { customData?: unknown }).customData;
  if (custom && typeof custom === "object") {
    const response = (custom as Record<string, unknown>)._serverResponse;
    // The SDK parses the envelope into an object here, so JSON is the only way
    // to see inside it without asserting a shape Firebase never promised.
    if (response !== undefined) {
      try {
        parts.push(JSON.stringify(response));
      } catch {
        // A response that cannot be serialised (a cycle) still leaves
        // `message` above to match on — better than losing the whole lookup.
      }
    }
  }
  return parts.join(" ");
}

/**
 * The `endUser` message key for a refusal — the sentence the person reads.
 *
 * Both existing keys were ported from the source app's sign-in and have been
 * dead ever since, because nothing here could tell the two cases apart. They
 * exist in English and German already, which is why this adds no third wording
 * for a case that already had one.
 */
export function signInRefusalKey(refusal: SignInRefusal): string {
  return refusal === "DEACTIVATED"
    ? "signIn.accountInactive"
    : "signIn.notAllowlisted";
}
